import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { ClientForm, UserForm } from "@/components/forms/ClientUserForms";
import { DeleteUserButton } from "@/components/DeleteUserButton";
import { ROLE_LABELS } from "@/lib/constants";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [client, setClient] = useState(null);
  const [products, setProducts] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [cratesTotal, setCratesTotal] = useState(0);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [archiving, setArchiving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, p, s, cr, u] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get("/products", { params: { client: id, page_size: 10 } }),
        api.get("/shipments", { params: { client: id, page_size: 10 } }),
        api.get("/crates", { params: { client: id, page_size: 1 } }),
        api.get("/users"),
      ]);
      setClient(c.data); setProducts(p.data.items || []);
      setShipments(s.data.items || []); setCratesTotal(cr.data.total || 0);
      setUsers((u.data || []).filter((x) => x.client_id === id));
    } catch (err) {
      if (err?.response?.status === 404) navigate("/clients");
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  async function toggleArchive() {
    if (!client) return;
    setArchiving(true);
    try { await api.post(`/clients/${client.id}/archive`, { archived: !client.is_archived }); await load(); }
    catch (err) { alert(apiError(err)); } finally { setArchiving(false); }
  }

  if (loading || !client) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-6">
      <Link to="/clients" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back to Organizations</Link>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold tracking-tight">{client.name}</h2>
            {client.is_archived && <Badge variant="secondary">Archived</Badge>}
          </div>
          <p className="text-muted-foreground">{client.contact_name || "No contact"}{client.contact_email ? ` · ${client.contact_email}` : ""}{client.contact_phone ? ` · ${client.contact_phone}` : ""}</p>
          {client.notes && <p className="mt-1 text-sm text-muted-foreground">{client.notes}</p>}
        </div>
        <div className="flex gap-2">
          <ClientForm client={client} onSaved={load} />
          <Button variant="outline" size="sm" onClick={toggleArchive} disabled={archiving}>
            {archiving && <Loader2 className="h-4 w-4 animate-spin" />}
            {client.is_archived ? "Restore" : "Archive"}
          </Button>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Products (active)</CardTitle></CardHeader><CardContent><p className="text-3xl font-bold">{products.length}</p></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Active Shipments</CardTitle></CardHeader><CardContent><p className="text-3xl font-bold">{shipments.filter((s) => s.status !== "Closed").length}</p></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Wooden Crates</CardTitle></CardHeader><CardContent><p className="text-3xl font-bold">{cratesTotal}</p></CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Organization Users</CardTitle></CardHeader><CardContent><p className="text-3xl font-bold">{users.length}</p></CardContent></Card>
      </div>
      <section className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Recent Products</h3>
        <Card><CardContent className="p-0">
          {products.length > 0 ? (
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-muted-foreground"><th className="px-4 py-3 font-medium">Serial Number</th><th className="px-4 py-3 font-medium">Type</th><th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3 font-medium">Location</th></tr></thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-b last:border-0 hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium">{p.serial_number}</td>
                    <td className="px-4 py-3">{p.product_type}</td>
                    <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                    <td className="px-4 py-3">{p.current_location}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="p-8 text-center text-muted-foreground">No products for this organization yet.</p>}
        </CardContent></Card>
      </section>
      <section className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Recent Shipments</h3>
        <Card><CardContent className="p-0">
          {shipments.length > 0 ? (
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-muted-foreground"><th className="px-4 py-3 font-medium">Shipment</th><th className="px-4 py-3 font-medium">Destination</th><th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3 font-medium">Created</th></tr></thead>
              <tbody>
                {shipments.map((s) => (
                  <tr key={s.id} className="border-b last:border-0 hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium">{s.shipment_code}</td>
                    <td className="px-4 py-3">{s.store_name} · {s.city}, {s.state}</td>
                    <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                    <td className="px-4 py-3 text-muted-foreground">{new Date(s.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="p-8 text-center text-muted-foreground">No shipments for this organization yet.</p>}
        </CardContent></Card>
      </section>
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Users</h3>
          {client && (
            <UserForm
              clients={[client]}
              presetClientId={client.id}
              onSaved={load}
              buttonSize="sm"
              buttonLabel="Add User"
            />
          )}
        </div>
        <Card><CardContent className="p-0">
          {users.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-slate-50/50 text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Email</th>
                  <th className="px-4 py-3 font-medium">Role</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b last:border-0 transition-colors hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium">{u.full_name || "—"}</td>
                    <td className="px-4 py-3">{u.email}</td>
                    <td className="px-4 py-3">{ROLE_LABELS[u.role]}</td>
                    <td className="px-4 py-3">{u.is_active ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Active</Badge> : <Badge variant="secondary">Pending</Badge>}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <UserForm clients={[client]} presetClientId={client.id} user={u} onSaved={load} />
                        {me?.id !== u.id && <DeleteUserButton user={u} onDeleted={load} />}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="p-8 text-center text-muted-foreground">No users for this organization yet. Click <span className="font-medium">Add User</span> above to invite the first one.</p>}
        </CardContent></Card>
      </section>
    </div>
  );
}
